import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    InputLabel,
    TextField,
    Typography,
} from "@mui/material";
import { useStyles } from "./RCAActionPlansFilters.styles";
import { useTranslation } from "react-i18next";
import { RCA_STATUSES } from "constants/constants";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import { useContext, useEffect, useState } from "react";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import SearchIcon from "@mui/icons-material/Search";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import { RCAActionPlanFiltersContext } from "contexts/RCAActionPlanFiltersContext/RCAActionPlanFiltersContext";
import DefaultFiltersButton from "pages/Corporate/RCA/components/DefaultFiltersButton/DefaultFiltersButton";

const RCAActionPlansFilters = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const {
        filters: filtersSelected,
        handleFiltersChange: onSetFilters,
        loadingDefaultOperations,
        isFiltersDefault,
        handleRemoveDefaultFilters,
        handleSetFiltersDefault,
        userFiltersDefault,
    } = useContext(RCAActionPlanFiltersContext);

    const [userIds, setUserIds] = useState<(string | number)[] | null>(null);
    const [statusIds, setStatusIds] = useState<(string | number)[] | null>(null);
    const [meetingTypeIds, setMeetingTypeIds] = useState<(string | number)[] | null>(null);

    useEffect(() => {
        if (userIds && statusIds && meetingTypeIds && !filtersSelected.totalMeetingTypes) {
            onSetFilters({
                ...filtersSelected,
                ownerIds: userIds,
                statusesIds: statusIds,
                meetingTypeIds: meetingTypeIds,
                totalMeetingTypes: meetingTypeIds.length,
            });
            setUserIds(null);
            setStatusIds(null);
        }
    }, [userIds, statusIds, onSetFilters, filtersSelected, meetingTypeIds]);

    return (
        <section className={classes.rcaIssuesFiltersMainWrapper}>
            <div className={`${mainClasses.contentHeaderActions} ${classes.mainFilters}`}>
                <span className={`${classes.title} ${mainClasses.contentTitle}`}>{t("rca.action_plans.title")}</span>
                <div className={classes.showCompletedCheck}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filtersSelected.showCompleted}
                                    value={filtersSelected.statusesIds.includes(RCA_STATUSES.COMPLETED)}
                                    color="info"
                                    onChange={(_, checked) => {
                                        onSetFilters({
                                            ...filtersSelected,
                                            showCompleted: checked,
                                            statusesIds: checked
                                                ? [...filtersSelected.statusesIds, RCA_STATUSES.COMPLETED]
                                                : filtersSelected.statusesIds.filter(
                                                      (statusId) => statusId !== RCA_STATUSES.COMPLETED,
                                                  ),
                                        });
                                    }}
                                />
                            }
                            label={t("rca.action_plans.filters.show_completed")}
                        />
                    </FormGroup>
                </div>

                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.executive_mill_filter_labels.owner")}
                        </Typography>
                    </InputLabel>
                    <RCAUsersDropdown
                        multiple
                        value={filtersSelected.ownerIds}
                        handleOnComponentMount={(userIds) => {
                            setUserIds(userIds);
                        }}
                        onChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                ownerIds: values,
                            });
                        }}
                    />
                </Box>
                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.executive_mill_filter_labels.status")}
                        </Typography>
                    </InputLabel>
                    <RCAStatusDropdown
                        multiple
                        hideLabel
                        fullWidth={false}
                        handleOnComponentMount={(statusIds) => {
                            setStatusIds(statusIds);
                        }}
                        value={filtersSelected.statusesIds}
                        asFilter
                        forActionPlan
                        excludeStatuses={filtersSelected.showCompleted ? [] : [RCA_STATUSES.COMPLETED]}
                        handleChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                statusesIds: values,
                            });
                        }}
                    />
                </Box>
                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.rca_library.rca_detail.meeting_type")}
                        </Typography>
                    </InputLabel>
                    <RCAMeetingTypesDropdown
                        notSelectedOption
                        multiple
                        value={filtersSelected.meetingTypeIds}
                        handleOnComponentMount={(meetingTypeIds) => {
                            setMeetingTypeIds(meetingTypeIds);
                        }}
                        onChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                meetingTypeIds: values,
                            });
                        }}
                    />
                </Box>
                <DefaultFiltersButton
                    isDefault={isFiltersDefault}
                    loading={loadingDefaultOperations}
                    hasUserDefault={!!userFiltersDefault}
                    onRemoveDefault={handleRemoveDefaultFilters}
                    onSaveOrUpdate={() => handleSetFiltersDefault(filtersSelected)}
                    variant="contained"
                    color="info"
                    className={mainClasses.contentButton}
                />
            </div>
            <div className={classes.searchBar}>
                <TextField
                    fullWidth
                    className={`${mainClasses.contentFormMultitextField}`}
                    name="description"
                    type="text"
                    variant="outlined"
                    value={filtersSelected.searchText}
                    placeholder={t("rca.action_plans.filters.search_keyword")}
                    onChange={(event) => {
                        onSetFilters({
                            ...filtersSelected,
                            searchText: event.target.value,
                        });
                    }}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>
        </section>
    );
};

export default RCAActionPlansFilters;
